import * as THREE from "three";
import { gsap, Power4 } from "gsap";
import { EventEmitter } from 'events';
import { InfoPanel } from "./InfoPanel";
import { Poem } from "../components/Poem";
import { WorldMarker } from "./WorldMarker";
import { Title } from "../components/Title";
import { Background } from "../components/Background";
import { ButtonParticles } from "../components/ButtonParticles";
import { Action, AppEvent, PanelMode, buttonsConfig, worlds } from "../constants";

export class UIPanels extends EventEmitter {
    constructor(assets) {
        super();
        this.currentHover = null;
        this._minimalMode = false;
        this._poemVisible = false;
        this._mode = PanelMode.NONE // "focus" "world" "library" "about"

        // Init three stuff
        this.mouse = new THREE.Vector2();
        const model = new THREE.Object3D();
        this.raycaster = new THREE.Raycaster();

        // Create all buttons
        this.buttons = buttonsConfig.map(({ id, icons, ButtonClass, aspectRatio }) => {
            const textureIds = icons.map(icon => `icon${icon}`);
            return new ButtonClass({ name: id, assets, textureIds, aspectRatio });
        });

        this.buttonMeshes = this.buttons.map(button => button.model);
        this.buttonMeshes.forEach(mesh => model.add(mesh));

        // Loop through the worlds config and create markers
        // @see constants.ts
        this.markers = worlds.map(({ name }) => {
            const iconId = "marker";
            const marker = new WorldMarker(`marker_${name}`, assets, iconId);
            model.add(marker.model);
            return marker;
        });
        this.markerMeshes = this.markers.map(marker => marker.model);

        // Utility component for handling particles
        this.buttonParticles = new ButtonParticles({ model });
        this.title = new Title({ assets, model });
        this.poem = new Poem({ assets, model });
        this.infoPanel = new InfoPanel({ assets, model });
        this.background = new Background({ assets, model });

        this.model = model;
        this.assets = assets;

        // Storing mouse click activity
        this.mouseDownPos = new THREE.Vector2()
        this.mouseUpPos = new THREE.Vector2()

        // Storing screen size
        this.screenHeight = 0;
        this.screenWidth = 0;

        // Show the sound button initially (buttons default to be hidden)
        this.soundBtn.show();

        //hide markers initially
        this.hideMarkers(0, true)

        this._allMarkersActivated = false;
    }

    activateWorld(w) {

        const marker = this.getMarkerByWorld(w);

        if (marker) {
            marker.activate();
            setTimeout(() => {
                this.buttonParticles.moveEmitterToMarker(marker);
            }, 2000);
        }
    }

    getMarkerByWorld(w) {
        const marker = this.markers.find(m => m.name === `marker_${w}`);
        return marker;
    }

    set mode(newMode) {
        // Don't bother if we are there already
        if (newMode === this._mode) {
            return;
        }

        // console.log("Changing UI Panel mode to", newMode);

        this._mode = newMode
        let showButtons = [];

        switch (newMode) {
            case PanelMode.LIBRARY:
                showButtons = ['info'];
                this.showMarkers();
                break;
            case PanelMode.FOCUS:
                showButtons = this._minimalMode ? ['info'] : ['explore', 'back', 'info'];
                break;
            case PanelMode.WORLD:
                showButtons = this._minimalMode ? ['quit'] : ['quit', 'previous', 'next'];
                break;
            case PanelMode.TRANSITION:
                showButtons = ['info'];
                break;
        }

        // Don't include SoundButton in this operation
        this.buttons
            .filter(b => b.name !== "sound")
            .forEach(btn => {
                const delay = (btn.name == 'previous' || btn.name == 'next') ? 17 : 0
                btn[showButtons.includes(btn.name) ? 'show' : 'hide'](delay)
            });
    }

    mouseMove(mousePos) {
        this.mouse.x = mousePos.px;
        this.mouse.y = - mousePos.py;
        this.raycast()
    }

    raycast() {
        this.raycaster.setFromCamera(this.mouse, this.model.parent);
        const meshes = this.buttonMeshes.concat(this.markerMeshes);

        const collidables = meshes.filter( el => {return el.visible})

        const intersects = this.raycaster.intersectObjects(collidables);

        if (intersects.length > 0) {
            const buttonModel = intersects[0].object.refButton;

            if (this.currentHover !== buttonModel) {
                if (this.currentHover) {
                    this.currentHover.mouseOut();
                }
                this.currentHover = buttonModel;
                this.currentHover.mouseOver();
            }
        } else if (this.currentHover) {
            this.currentHover.mouseOut()
            this.currentHover = null;
        }
    }

    mouseDown(mousePos) {
        this.mouseDownPos = new THREE.Vector2(mousePos.px, mousePos.py);
    }

    mouseUp(mousePos) {
        this.mouseUpPos = new THREE.Vector2(mousePos.px, mousePos.py);

        if (this.currentHover) {

            let eventName = this.currentHover.name;

            if (eventName == "info") {
                // Just clarify whether it's opening or closing

                if (this.infoPanel._isOpen) {
                    this.closeInfoPanel();
                } else {
                    this.openInfoPanel()
                }
            } else {
                this.emit(AppEvent.UI_ACTION, eventName);
            }

            this.buttonParticles.moveEmitterToHover(this.currentHover);
        } else {
            /*
            we clicked somewhere else on the screen
            go to next step but first we need to check
            if the user didn't just use the orbit control
            to turn around a spot
            */
            const mouseDiplacement = new THREE.Vector2().subVectors(this.mouseDownPos, this.mouseUpPos)
            if (mouseDiplacement.length() < .01) {
                this.emit(AppEvent.UI_ACTION, 'next');
            }
        }
    }

    setMinimalMode(state) {
        this._minimalMode = state;
        if (this._poemVisible) {
            this.hidePoem();
        }
    }

    toggleInfoPanel() {
        if (this.infoPanel._isOpen) {
            this.closeInfoPanel();
        } else {
            this.openInfoPanel()
        }
    }

    closeInfoPanel() {
        if (!this.infoPanel._isOpen) return;
        this.infoPanel.toggle();
        const eventName = Action.INFO_CLOSE;
        const infoButton = this.buttons.find(item => item.name === 'info')
        infoButton.toggle(false)
        this.emit(AppEvent.UI_ACTION, eventName);
    }

    openInfoPanel() {
        if (this.infoPanel._isOpen) {
            return;
        }
        this.infoPanel.toggle();
        const eventName = Action.INFO;
        const infoButton = this.buttons.find(item => item.name === 'info')
        infoButton.toggle(true)
        this.emit(AppEvent.UI_ACTION, eventName);
    }

    loop(params) {
        this.buttons.forEach(b => b.loop(params));

        if (this.buttonParticles) {
            this.buttonParticles.update(params.deltaTime);
        }

        if (this.title.active) {
            this.title.update(params.time);
        }
        //*
        if (this.poem.active) {
            this.poem.update(params.time);
        }
        //*/

        this.background.update(params.time);

        if (this.infoPanel.active) {
            this.infoPanel.update(params.time);
        }
    }

    resize(w, h) {

        this.screenHeight = h;
        this.screenWidth = w;

        // Refer to config for button positions
        buttonsConfig.forEach((buttonConfig, index) => {
            const { absolute, relative, scale } = buttonConfig.position;
            const b = this.buttons[index];
            b.model.position.x = absolute[0] + (w * relative[0]);
            b.model.position.y = absolute[1] + (h * relative[1]);
            b.model.position.z = absolute[2]
            b.model.scale.set(scale, scale, scale);
        });

        this.markers.forEach((marker, i) => {
            const mPos = marker.model.position;
            mPos.x = (i * 0.023) - ((this.markers.length - 1) * 0.5 * 0.023);
            mPos.y = - 0.05 + h * 0.5;
            mPos.z = - 0.005;
        });

        this.background.resize(w, h);
        this.infoPanel.resize(w, h);
    }

    showMarkers() {
        let models = []
        this.markers.forEach((marker, i) => {
            marker.model.visible = true
            gsap.killTweensOf(marker.model.scale)
            gsap.to(marker.model.scale, {
                x: 1,
                y: 1,
                z: 1,
                delay: i * .1,
                duration: 2,
                ease: Power4.easeOut,
            })
        });

    }

    hideMarkers(delay, instant = false) {
        this.markers.forEach((marker, i) => {
            gsap.killTweensOf(marker.model.scale)

            if (instant) {
                marker.model.scale.set(0, 0, 0)
                marker.model.visible = false;
            } else {
                gsap.to(marker.model.scale, {
                    x: 0,
                    y: 0,
                    z: 0,
                    duration: 2,
                    delay: delay + i * .1,
                    ease: Power4.easeOut,
                    onComplete: () => {
                        marker.model.visible = false
                    }
                })
            }
        })
    }

    showTitle(params) {
        this.title.show(params);
    }

    hideTitle() {
        this.title.hide();
    }

    showPoem(params) {
        this.poem.show(params);
        this._poemVisible = true;
    }

    hidePoem() {
        this.poem.hide();
        this._poemVisible = false;
    }

    get soundBtn() {
        return this.buttons.find(b => b.name === "sound");
    }

    get infoBtn() {
        return this.buttons.find(b => b.name === "info");
    }

    get mode() {
        return this._mode
    }

    get allMarkersActivated() {
        return this.markers.every(marker => marker.activated);
    }
}